// Guardas de rota por sessão e papel. A API é a autoridade (403/404); os guardas só evitam telas inúteis.
// Regra: nunca mandar de uma área para a outra sem ter certeza de que o papel é da outra. Antes, um papel que o
// front não conhecia (RECEPCAO/MASTER/PROFESSOR_LIDER num app em cache de versão anterior) ia de "/painel" para
// "/" e de volta, milhares de vezes, e a tela ficava branca. Papel desconhecido agora vê `ContaSemArea`.
// Os guardas só do painel (ExigirEquipe, ExigirPermissao) ficam em guardas.painel.tsx: dependem do menu do
// painel e não podem entrar no bundle do app nativo (ADR app nativo, D6).
import { useEffect, useState, type ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { ehEquipe, inicioDoPapel, useSair, useSessao } from "./api/sessao";
import { useOnline } from "./compartilhado/lib/hooks";
import { Botao, ErroCarga, Icones, SimboloGS, Vazio } from "./compartilhado/ui";
import { deveMostrarSemInternet, LIMITE_SEM_INTERNET_MS } from "./semInternet";

/** Carregamento inicial do app (splash com o símbolo — DESIGN §4). */
export function Splash() {
  return (
    <div className="grid min-h-dvh place-items-center bg-parede" aria-busy="true">
      <SimboloGS className="h-10 w-auto text-tinta" titulo="GrindSet" />
      <span className="sr-only">Carregando</span>
    </div>
  );
}

export function useVolta(): string {
  const { pathname, search } = useLocation();
  return encodeURIComponent(pathname + search);
}

/** Conta logada sem área neste app (papel que esta versão não conhece): explica e oferece sair/recarregar. */
export function ContaSemArea() {
  const sair = useSair();
  return (
    <div className="grid min-h-dvh place-items-center bg-parede px-margem" data-conta-sem-area="">
      <Vazio
        icone={Icones.bloqueado}
        titulo="Não deu para abrir sua área"
        texto="Seu acesso é novo ou o app está desatualizado. Recarregue; se continuar, fale com o gerente."
        acao={
          <div className="flex flex-col gap-3 sm:flex-row">
            <Botao variante="primario" onClick={() => window.location.reload()}>
              Recarregar
            </Botao>
            <Botao
              variante="secundario"
              carregando={sair.isPending}
              onClick={() => sair.mutate(undefined, { onSettled: () => window.location.assign("/painel/entrar") })}
            >
              Sair
            </Botao>
          </div>
        }
      />
    </div>
  );
}

export function ErroSessao({ aoTentar }: { aoTentar: () => void }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-parede px-margem">
      <ErroCarga texto="Não deu para verificar sua sessão." aoTentar={aoTentar} />
    </div>
  );
}

/**
 * Abertura sem internet (semInternet.ts): ocupa a tela toda, por cima do aviso "Sem conexão" do topo (Reconectando,
 * z-60), que diria o mesmo. Quando a rede volta, a consulta da sessão continua sozinha (o TanStack retoma o que
 * ficou em pausa); "Tentar de novo" força agora.
 */
export function SemInternet({ aoTentar }: { aoTentar: () => void }) {
  return (
    <div className="fixed inset-0 z-70 grid place-items-center bg-parede px-margem" data-sem-internet="">
      <ErroCarga texto="Sem internet. Conecte-se para entrar." aoTentar={aoTentar} />
    </div>
  );
}

/** Estado da abertura: a sessão chegou, ou falta e a rede falhou ("Sem internet" em até 5 s). */
function useSessaoNaAbertura() {
  const consulta = useSessao();
  const online = useOnline();
  const [passouLimite, setPassouLimite] = useState(false);
  useEffect(() => {
    if (!consulta.isPending) return;
    const t = setTimeout(() => setPassouLimite(true), LIMITE_SEM_INTERNET_MS);
    return () => clearTimeout(t);
  }, [consulta.isPending]);
  const semInternet = deveMostrarSemInternet({
    temResposta: consulta.data !== undefined,
    falhou: consulta.isError,
    erro: consulta.failureReason ?? consulta.error,
    online,
    passouLimite,
  });
  return { ...consulta, semInternet };
}

/**
 * App do aluno: exige papel ALUNO. Sem termos aceitos ou onboarding pendente -> /boas-vindas/1
 * (exceto quando `permitirOnboarding`, usado nas próprias telas de onboarding/instalação).
 */
export function ExigirAluno({
  permitirOnboarding = false,
  children,
}: {
  permitirOnboarding?: boolean;
  children?: ReactNode;
}) {
  const { data: sessao, isPending, isError, refetch, semInternet } = useSessaoNaAbertura();
  const volta = useVolta();
  if (semInternet) return <SemInternet aoTentar={() => void refetch()} />;
  if (isPending) return <Splash />;
  if (isError) return <ErroSessao aoTentar={() => void refetch()} />;
  if (!sessao) return <Navigate to={`/entrar?volta=${volta}`} replace />;
  if (sessao.usuario.papel !== "ALUNO")
    return ehEquipe(sessao.usuario.papel) ? <Navigate to="/painel" replace /> : <ContaSemArea />;
  const pendente = !sessao.aluno?.termosAceitos || !sessao.aluno.onboardingConcluido;
  if (pendente && !permitirOnboarding) return <Navigate to="/boas-vindas/1" replace />;
  return <>{children ?? <Outlet />}</>;
}

/** Telas de login: quem já está logado vai para a própria área (ou para `volta`). */
export function SomenteVisitante({ area, children }: { area: "aluno" | "equipe"; children?: ReactNode }) {
  const { data: sessao, isPending, refetch, semInternet } = useSessaoNaAbertura();
  const { search } = useLocation();
  if (semInternet) return <SemInternet aoTentar={() => void refetch()} />;
  if (isPending) return <Splash />;
  if (sessao) {
    const papelDaArea = area === "aluno" ? sessao.usuario.papel === "ALUNO" : ehEquipe(sessao.usuario.papel);
    const volta = new URLSearchParams(search).get("volta");
    const destino =
      papelDaArea && volta?.startsWith("/") && !volta.startsWith("//")
        ? volta
        : inicioDoPapel(sessao.usuario.papel);
    return <Navigate to={destino} replace />;
  }
  return <>{children ?? <Outlet />}</>;
}
